import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useStore } from '../store/useStore';
import { PartialThemeColors, ThemeMode, ThemePreset } from '../types/theme';
import {
  ThemeColors,
  defaultUserPreferences
} from '../types/user';
import { defaultThemeColors } from './defaultThemeColors';
import { generateThemeVariants } from '../utils/themeUtils';
import { useErrorFeedback } from '../hooks/useErrorFeedback';
import { motion, AnimatePresence } from 'framer-motion';


interface ThemeContextType {
  colors: ThemeColors;
  mode: ThemeMode;
  isDarkMode: boolean;
  customThemes: ThemePreset[];
  setMode: (mode: ThemeMode) => void;
  updateColors: (colors: PartialThemeColors) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

// Define useTheme hook first to avoid TDZ (Temporal Dead Zone) issues
// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  // Ensure colors always have fallback values to prevent undefined errors in production
  const safeColors = {
    ...defaultThemeColors,
    ...context.colors
  };
  
  return {
    ...context,
    colors: safeColors
  };
};

interface ThemeContextProps {
  children: React.ReactNode
}

const ThemeTransition = ({ children }: { children: React.ReactNode }) => {
  const { isDarkMode } = useTheme();

  return (
    <motion.div
      key={isDarkMode ? 'dark' : 'light'}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.26, ease: [0.4, 0, 0.2, 1] }}
      style={{ width: '100%', height: '100%' }}
    >
      {children}
    </motion.div>
  );
};

export function ThemeProvider({ children }: ThemeContextProps) {
  const { user, updatePreferences } = useStore()
  const { showError } = useErrorFeedback()
  
  // Initialize theme state
  const [state, setState] = useState(() => {
    // Get system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    // Priority: 
    // 1. User preferences from store
    // 2. localStorage preference
    // 3. System preference
    
    const savedTheme = localStorage.getItem('theme-preference');
    const userTheme = user?.preferences?.theme;
    const themeMode = userTheme || savedTheme as ThemeMode || 'system';
    
    // Determine if dark mode should be enabled
    const isDark = themeMode === 'dark' || 
      (themeMode === 'system' && prefersDark);

    // Get theme colors

    return {
      isDarkMode: isDark,
      mode: themeMode
    };
  });

  // Memoize colors to prevent unnecessary regeneration
  const memoizedColors = useMemo(() => {
    const baseColors = user?.preferences?.themeColors || defaultThemeColors;
    // Double-check that we have valid color values
    const safeBaseColors = { ...defaultThemeColors, ...baseColors };
    
    const generatedColors = generateThemeVariants(safeBaseColors, state.isDarkMode);
    
    // Ensure all required properties exist with fallbacks
    return {
      ...defaultThemeColors,
      ...generatedColors
    };
  }, [user?.preferences?.themeColors, state.isDarkMode]);

  // Update effect for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      if (state.mode === 'system') {
        setState(prev => ({
          ...prev,
          isDarkMode: e.matches
        }));
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [state.mode]);

  // Sync with user preferences when they change
  useEffect(() => {
    if (user?.preferences) {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const isDark = user.preferences.theme === 'dark' ||
        (user.preferences.theme === 'system' && prefersDark);

      setState(prev => ({
        ...prev,
        isDarkMode: isDark,
        mode: user.preferences.theme || prev.mode
      }));
    }
  }, [user?.preferences]);

  // Update CSS variables whenever memoized colors change
  useEffect(() => {
    updateCssVariables(memoizedColors);
  }, [memoizedColors]);

  // Update data-theme attribute
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', state.isDarkMode ? 'dark' : 'light');
  }, [state.isDarkMode]);

  const value = {
    colors: memoizedColors,
    mode: state.mode,
    isDarkMode: state.isDarkMode,
    customThemes: user?.preferences?.customThemes || [],
    setMode: async (mode: ThemeMode) => {
      try {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        const isDark = mode === 'dark' || (mode === 'system' && prefersDark);
        
        // First, update the CSS variables with transition
        document.documentElement.style.setProperty('--theme-transition', '0.3s');
        
        // Update local state first
        setState(prev => ({
          ...prev,
          isDarkMode: isDark,
          mode
        }));

        // Store in localStorage
        localStorage.setItem('theme-preference', mode);

        // Then update user preferences if logged in
        if (user?.preferences) {
          await updatePreferences({
            ...user.preferences,
            theme: mode
          });
        }

        // Remove transition after animation completes
        setTimeout(() => {
          document.documentElement.style.removeProperty('--theme-transition');
        }, 300);
      } catch (error) {
        showError(error instanceof Error ? error : new Error('Failed to update theme'), 'ThemeContext');
      }
    },
    updateColors: async (newColors: PartialThemeColors) => {
      try {
        // Update local state immediately
        const updatedColors = {
          ...memoizedColors,
          ...newColors,
          logo: memoizedColors.logo
        };
        
        console.log('Updating colors with:', updatedColors);
        
        // Update user preferences immediately so the memoized colors will update
        if (user?.preferences) {
          const updatedPreferences = {
            ...defaultUserPreferences,
            ...user.preferences,
            themeColors: updatedColors,
            theme: state.mode    
          };

          await updatePreferences(updatedPreferences);
        }

        // Store in localStorage as fallback
        try {
          localStorage.setItem('app-theme', JSON.stringify({
            mode: state.mode,
            colors: updatedColors
          }));
        } catch (e) {
          console.warn('Failed to save theme to localStorage:', e);
        }
      } catch (error) {
        console.error('Theme update error:', error);
        if (error instanceof Error) {
          console.error('Error details:', {
            message: error.message,
            stack: error.stack,
            name: error.name
          });
        }
        showError(error instanceof Error ? error : new Error('Failed to update colors'), 'ThemeContext');
        
        // The memoized colors will handle reverting automatically
      }
    }
  };

  return (
    <ThemeContext.Provider value={value}>
      <AnimatePresence mode="wait">
        <ThemeTransition>
          {children}
        </ThemeTransition>
      </AnimatePresence>
    </ThemeContext.Provider>
  );
}

// Helper function to update CSS variables
function updateCssVariables(colors: ThemeColors) {
  if (!colors) return; // Safety check
  
  const root = document.documentElement;
  document.body.style.backgroundColor = colors.background || defaultThemeColors.background;
  
  // Process colors recursively
  function processColors(obj: Record<string, string | Record<string, string>>, prefix = '') {
    if (!obj) return; // Safety check for undefined objects
    
    Object.entries(obj).forEach(([key, value]) => {
      if (typeof value === 'string' && value) { // Check value is not empty
        root.style.setProperty(`--color-${prefix}${key}`, value);
        if (value.startsWith('#')) {
          const rgb = hexToRgb(value);
          if (rgb) {
            root.style.setProperty(
              `--color-${prefix}${key}-rgb`,
              `${rgb.r}, ${rgb.g}, ${rgb.b}`
            );
          }
        }
      } else if (typeof value === 'object' && value !== null) {
        processColors(value, `${key}-`);
      }
    });
  }

  processColors(colors as unknown as Record<string, string | Record<string, string>>);
}

// Helper function to convert hex to rgb
function hexToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}
